/**
 * „?" neben einer Beschriftung — ein Klick zeigt die Erklärung, ein Klick
 * daneben oder Esc schließt sie.
 *
 * Bewusst ein Klick und kein Hover-`title`: Der Text ist oft mehrere Sätze lang,
 * ein Tooltip verschwindet beim Lesen, und auf dem Tablet gibt es kein Hover.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { HelpCircle } from 'lucide-react';

export function Hilfe({ children, titel = 'Erklärung' }: { children: ReactNode; titel?: string }) {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!offen) return;
    const klick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOffen(false);
    };
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOffen(false);
    };
    document.addEventListener('mousedown', klick);
    document.addEventListener('keydown', taste);
    return () => {
      document.removeEventListener('mousedown', klick);
      document.removeEventListener('keydown', taste);
    };
  }, [offen]);

  return (
    <span ref={ref} className="relative inline-flex align-middle">
      <button
        type="button"
        // Liegt oft in einem <label>: Der Klick darf das Feld nicht fokussieren.
        onClick={(e) => {
          e.preventDefault();
          setOffen((v) => !v);
        }}
        className="rounded-full text-muted-foreground transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        aria-label={titel}
        aria-expanded={offen}
        tabIndex={-1}
      >
        <HelpCircle className="h-3.5 w-3.5" />
      </button>
      {offen && (
        <span
          role="tooltip"
          className="absolute left-0 top-5 z-50 block w-72 rounded-lg border border-border p-3 text-left text-xs font-normal leading-relaxed shadow-lg"
          // Inline gegen die Popover-Transparenz (FORM-STYLES.md → Overlays)
          style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
        >
          {children}
        </span>
      )}
    </span>
  );
}
